import { HttpClient, HttpHeaders } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { AuthUtils } from './auth.utils';
import { UserService } from '../user/user.service';
import { catchError, Observable, of, switchMap, throwError } from 'rxjs';
import { environment } from '../../../environments/environment';
import { Login } from '../../interfaces/Login';
import { User } from '../user/user.types';
import { Menu } from '../../interfaces/Menu';
import { AuthenticationResponse } from '../../interfaces/AuthenticationResponse';
import { EmailToken } from '../../interfaces/EmailToken';
import { JwtHelperService } from '@auth0/angular-jwt';

@Injectable({ providedIn: 'root' })
export class AuthService {
    private _authenticated: boolean = false;
    private _httpClient = inject(HttpClient);
    private _userService = inject(UserService);
    private _url: string = environment.apiUrlBase;

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Setter & getter for temporal token
     */
    set temporalToken(token: string) {
        localStorage.setItem('temporalToken', token);
    }

    get temporalToken(): string {
        return localStorage.getItem('temporalToken') ?? '';
    }

    /**
     * Setter & getter for access token
     */
    set accessToken(token: string) {
        localStorage.setItem('accessToken', token);
    }

    get accessToken(): string {
        return localStorage.getItem('accessToken') ?? '';
    }

    /**
     * Setter & getter for role name
     */
    set roleName(roleName: string) {
        localStorage.setItem('roleName', roleName);
    }

    get roleName(): string {
        return localStorage.getItem('roleName') ?? '';
    }

    /**
     * Setter & getter for company name
     */
    set companyName(companyName: string) {
        localStorage.setItem('companyName', companyName);
    }

    get companyName(): string {
        return localStorage.getItem('companyName') ?? '';
    }

    /**
     * Setter & getter for user
     */
    set user(user: string) {
        localStorage.setItem('user$', user);
    }

    get user(): string {
        return localStorage.getItem('user$') ?? '';
    }

    /**
     * Setter & getter for asm token
     */
    set asmToken(token: string) {
        localStorage.setItem('asmToken', token);
    }

    get asmToken(): string {
        return localStorage.getItem('asmToken') ?? '';
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Forgot password
     *
     * @param email
     */
    forgotPassword(email: string): Observable<any> {
        let credentials: Login = { email: email, password: '', recaptcha: '' };
        return this._httpClient
            .post(`${this._url}/v1/passwordReset`, credentials)
            .pipe(
                switchMap((response: any) => {
                    console.log('forgotPassword OK ', response);
                    // Return a new observable with the response
                    return of(true);
                })
            );
    }

    /**
     * Reset password
     *
     * @param password
     */
    resetPassword(token: string, password: string): Observable<any> {
        let credentials: Login = { email: '', password: password, recaptcha: '' };
        return this._httpClient
            .post(`${this._url}/v1/passwordReset/${token}`, credentials)
            .pipe(
                switchMap((response: any) => {
                    console.log('resetPassword OK ', response);
                    // Return a new observable with the response
                    return of(true);
                })
            );
    }

    /**
     * Sign in
     *
     * @param credentials
     */
    signIn(credentials: Login): Observable<AuthenticationResponse> {
        // Throw error, if the user is already logged in
        if (this._authenticated) {
            return throwError(() => new Error('User is already logged in.'));
        }

        return this._httpClient
            .post<AuthenticationResponse>(`${this._url}/v1/login`, credentials)
            .pipe(
                switchMap((response: AuthenticationResponse) => {
                    // Store the temporal token in the local storage
                    this.temporalToken = response.token;

                    // Store the company name in the local storage
                    this.companyName = response.companyName;

                    // Return a new observable with the response
                    return of(response);
                })
            );
    }

    /**
     * MFA Sign in
     *
     * @param temporalToken
     */
    mfaSignIn(temporalToken: EmailToken): Observable<AuthenticationResponse> {
        // Throw error, if the user is already logged in
        if (this._authenticated) {
            return throwError(() => new Error('User is already logged in.'));
        }
        const headers = new HttpHeaders({
            Authorization: 'Bearer ' + this.temporalToken,
        });
        return this._httpClient
            .post<AuthenticationResponse>(`${this._url}/v1/login/mfa`, temporalToken, { headers })
            .pipe(
                switchMap((response: AuthenticationResponse) => {
                    const helper = new JwtHelperService();
                    const jwtToken = response.token;
                    const decodedToken = helper.decodeToken(jwtToken);

                    // Remove the temporal token from the local storage
                    localStorage.removeItem('temporalToken');

                    // Store the access token in the local storage
                    this.accessToken = jwtToken;

                    // Store the role in the local storage
                    this.roleName = response.role;

                    // Store the company name in the local storage
                    this.companyName = response.companyName;

                    // Set the authenticated flag to true
                    this._authenticated = true;

                    // Store the asm token in the local storage
                    this.asmToken = response.access;

                    // Store the user on the user service
                    let _menu: Menu[] = JSON.parse(response.menu);
                    let _user: User = {
                        userName: response.userName,
                        role: response.role,
                        menu: _menu,
                        slug: decodedToken.slug ? decodedToken.slug : 'default'
                    };
                    this.user = JSON.stringify(_user);

                    this._userService.user = _user;

                    // Return a new observable with the response
                    return of(response);
                })
            );
    }

    /**
     * Sign in using the access token
     */
    signInUsingToken(): Observable<any> {
        // Sign in using the token
        if (this.accessToken) {
            // Set the authenticated flag to true
            this._authenticated = true;

            // Store the user on the user service
            this._userService.user = JSON.parse(this.user);

            // Return true
            return of(true);
        } else {
            return of(false);
        }

        /*
        const headers = new HttpHeaders({
            Authorization: 'Bearer ' + this.accessToken,
        });
        return this._httpClient
            .get(`${this._url}/health`, {
                headers,
            })
            .pipe(
                catchError(() =>
                    // Return false
                    of(false)
                ),
                switchMap((response: any) => {
                    // Set the authenticated flag to true
                    this._authenticated = true;

                    // Store the user on the user service
                    this._userService.user = JSON.parse(this.user);

                    // Return true
                    return of(true);
                })
            );
            */
    }

    /**
     * Sign out
     */
    signOut(): Observable<any> {
        // Remove the user from the local storage
        localStorage.removeItem('user$');
        // Remove the role name from the local storage
        localStorage.removeItem('roleName');
        // Remove the company name from the local storage
        localStorage.removeItem('companyName');
        // Remove the access token from the local storage
        localStorage.removeItem('accessToken');
        // Remove the asm token from the local storage
        localStorage.removeItem('asmToken');
        // Set the authenticated flag to false
        this._authenticated = false;
        // Return the observable
        return of(true);
    }

    /**
     * Sign up
     *
     * @param user
     */
    signUp(user: {
        name: string;
        email: string;
        password: string;
        company: string;
    }): Observable<any> {
        return this._httpClient.post('api/auth/sign-up', user);
    }

    /**
     * Unlock session
     *
     * @param credentials
     */
    unlockSession(credentials: Login): Observable<any> {
        return this._httpClient.post('api/auth/unlock-session', credentials);
    }

    /**
     * Check the authentication status
     */
    check(): Observable<boolean> {
        // Check if the user is logged in
        if (this._authenticated) {
            return of(true);
        }

        // Check the access token availability
        if (!this.accessToken) {
            return of(false);
        }

        // Check the access token expire date
        if (AuthUtils.isTokenExpired(this.accessToken)) {
            return of(false);
        }

        // If the access token exists, and it didn't expire, sign in using it
        return this.signInUsingToken();
    }
}
